import { Subscription } from 'rxjs';
import { Signaling, ServerManifest } from './Signaling';
import firebaseService from './firebaseService';
import {
    encryptServerFile,
    decryptServerFile,
    readServerMetadata,
    serverCapability,
} from '../Utils/serverTransferCrypto';
import type { FileMetadata } from './FileReceiver';

type Callbacks = {
    activate: () => void;
    sendProgress: (id: number, progress: number, bytes: number) => void;
    metadata: (id: number, metadata: FileMetadata) => void;
    received: (id: number, file: File) => void;
    receiveProgress: (id: number, progress: number, bytes: number) => void;
    error: (id: number | null, error: Error) => void;
};

type Operation = { controller: AbortController; objectId?: string };

export class ServerTransfer {
    public active = false;
    private disposed = false;
    private subscription: Subscription;
    private capability: Promise<string>;
    private sends = new Map<number, Operation>();
    private downloads = new Set<AbortController>();
    private processed = new Set<string>();
    private receiving = false;
    private announced = new Map<string, FileMetadata>();
    private receiptWaiters = new Map<string, (status: string) => void>();

    constructor(
        private signaling: Signaling,
        private key: CryptoKey,
        private callbacks: Callbacks,
    ) {
        this.capability = serverCapability(key);
        // Capability failures are surfaced by send/download; prevent an idle rejection.
        void this.capability.catch(() => undefined);
        this.subscription = signaling.serverState.subscribe((state) => {
            if (this.disposed || !state.enabled) return;
            if (!this.active) {
                this.active = true;
                // Let the owning view attach its callbacks before activation.
                queueMicrotask(() => {
                    if (this.disposed) return;
                    callbacks.activate();
                    void firebaseService.logEvent(
                        'transfer_transport_selected',
                        {
                            session_id: signaling.id,
                            transport: 'server',
                            role: signaling.localRole,
                        },
                    );
                });
            }
            for (const [id, manifest] of Object.entries(
                state.transfers || {},
            )) {
                if (
                    manifest.status !== 'ready' &&
                    manifest.status !== 'uploading'
                )
                    this.receiptWaiters.get(id)?.(manifest.status);
            }
            void this.receiveNext();
        });
    }

    private async api<T>(
        action: string,
        extra: Record<string, unknown> = {},
    ): Promise<T> {
        return firebaseService.serverTransfer<T>({
            action,
            sessionId: this.signaling.id,
            capability: await this.capability,
            ...extra,
        });
    }

    private check(controller: AbortController) {
        if (this.disposed || controller.signal.aborted)
            throw new Error('CANCELLED');
    }

    private request(
        method: 'PUT' | 'GET',
        url: string,
        controller: AbortController,
        progress: (loaded: number, total: number) => void,
        body?: Blob,
    ): Promise<Blob> {
        return new Promise((resolve, reject) => {
            this.check(controller);
            const xhr = new XMLHttpRequest();
            xhr.open(method, url);
            xhr.responseType = 'blob';
            xhr.timeout = 10 * 60 * 1000;
            if (method === 'PUT') {
                xhr.setRequestHeader(
                    'Content-Type',
                    'application/octet-stream',
                );
                xhr.setRequestHeader('x-goog-if-generation-match', '0');
                xhr.setRequestHeader('Cache-Control', 'private, no-store');
            }
            const abort = () => xhr.abort();
            const cleanup = () =>
                controller.signal.removeEventListener('abort', abort);
            controller.signal.addEventListener('abort', abort, { once: true });
            (method === 'PUT' ? xhr.upload : xhr).onprogress = (event) =>
                progress(event.loaded, event.total);
            xhr.onload = () => {
                cleanup();
                if (xhr.status >= 200 && xhr.status < 300)
                    resolve(xhr.response);
                else
                    reject(
                        new Error('Transfer interrupted. Please try again.'),
                    );
            };
            xhr.onerror = () => {
                cleanup();
                reject(new Error('Connection lost. Please try again.'));
            };
            xhr.ontimeout = () => {
                cleanup();
                reject(new Error('Transfer timed out. Please try again.'));
            };
            xhr.onabort = () => {
                cleanup();
                reject(new Error('CANCELLED'));
            };
            xhr.send(body);
        });
    }

    public async sendFile(file: File, id: number) {
        const operation: Operation = { controller: new AbortController() };
        this.sends.set(id, operation);
        try {
            const encrypted = await encryptServerFile(this.key, file, id, () =>
                this.check(operation.controller),
            );
            const reservation = await this.api<{
                objectId: string;
                url: string;
            }>('create', {
                bytes: encrypted.blob.size,
                metadata: encrypted.metadata,
                role: this.signaling.localRole,
            });
            operation.objectId = reservation.objectId;
            this.check(operation.controller);
            await this.request(
                'PUT',
                reservation.url,
                operation.controller,
                (loaded) => {
                    const ratio = loaded / encrypted.blob.size;
                    this.callbacks.sendProgress(
                        id,
                        Math.min(95, Math.round(ratio * 95)),
                        Math.min(file.size, Math.round(ratio * file.size)),
                    );
                },
                encrypted.blob,
            );
            this.check(operation.controller);
            await this.api('publish', { objectId: reservation.objectId });
            await this.waitForReceipt(
                reservation.objectId,
                operation.controller,
            );
            this.check(operation.controller);
            this.callbacks.sendProgress(id, 100, file.size);
        } catch (error) {
            if (operation.objectId)
                void this.api('cancel', { objectId: operation.objectId }).catch(
                    () => undefined,
                );
            throw error;
        } finally {
            this.sends.delete(id);
        }
    }

    private waitForReceipt(
        objectId: string,
        controller: AbortController,
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            let timer: ReturnType<typeof setTimeout>;
            const finish = (status: string) => {
                clearTimeout(timer);
                this.receiptWaiters.delete(objectId);
                controller.signal.removeEventListener('abort', abort);
                if (status === 'received') resolve();
                else
                    reject(
                        new Error(
                            status === 'cancelled'
                                ? 'CANCELLED'
                                : 'The other device could not receive this file. Please try again.',
                        ),
                    );
            };
            const abort = () => finish('cancelled');
            timer = setTimeout(() => finish('timeout'), 10 * 60 * 1000);
            this.receiptWaiters.set(objectId, finish);
            controller.signal.addEventListener('abort', abort, { once: true });
            if (controller.signal.aborted || this.disposed) abort();
            else {
                const status =
                    this.signaling.serverState.value.transfers?.[objectId]
                        ?.status;
                if (status && status !== 'ready' && status !== 'uploading')
                    finish(status);
            }
        });
    }

    private async receiveNext() {
        if (this.receiving || this.disposed || !this.active) return;
        this.receiving = true;
        try {
            while (!this.disposed) {
                const entry = Object.entries(
                    this.signaling.serverState.value.transfers || {},
                ).find(
                    ([id, m]) =>
                        m.role !== this.signaling.localRole &&
                        !this.processed.has(id) &&
                        (m.status === 'ready' ||
                            (m.status === 'uploading' &&
                                !this.announced.has(id)) ||
                            ((m.status === 'cancelled' ||
                                m.status === 'failed') &&
                                this.announced.has(id))),
                );
                if (!entry) break;
                const [objectId, manifest] = entry;
                if (manifest.status === 'uploading') {
                    try {
                        const response = await this.api<{ metadata: string }>(
                            'metadata',
                            { objectId },
                        );
                        const metadata = await readServerMetadata(
                            this.key,
                            response.metadata,
                        );
                        if (this.disposed) return;
                        this.announced.set(objectId, metadata);
                        this.callbacks.metadata(metadata.id, {
                            ...metadata,
                            uploadPending: true,
                        });
                    } catch (err) {
                        this.processed.add(objectId);
                        if (!this.disposed)
                            this.callbacks.error(
                                null,
                                err instanceof Error
                                    ? err
                                    : new Error(
                                          'Could not receive file details.',
                                      ),
                            );
                    }
                    continue;
                }
                this.processed.add(objectId);
                if (manifest.status === 'ready') {
                    await this.receive(objectId, manifest);
                } else {
                    const metadata = this.announced.get(objectId)!;
                    this.callbacks.error(
                        metadata.id,
                        new Error(
                            'The sender stopped this transfer. Ask them to send the file again.',
                        ),
                    );
                }
                this.announced.delete(objectId);
            }
        } finally {
            this.receiving = false;
        }
    }

    private async receive(objectId: string, manifest: ServerManifest) {
        const controller = new AbortController();
        this.downloads.add(controller);
        let id: number | null = this.announced.get(objectId)?.id ?? null;
        let delivered = false;
        try {
            if (manifest.expiresAt <= Date.now())
                throw new Error('This file has expired. Please send it again.');
            const response = await this.api<{ url: string; metadata: string }>(
                'download',
                { objectId },
            );
            this.check(controller);
            // Use authenticated backend metadata, not untrusted signaling contents.
            const metadata = await readServerMetadata(
                this.key,
                response.metadata,
            );
            id = metadata.id;
            this.callbacks.metadata(id, { ...metadata, uploadPending: false });
            const blob = await this.request(
                'GET',
                response.url,
                controller,
                (loaded, total) => {
                    if (total)
                        this.callbacks.receiveProgress(
                            metadata.id,
                            Math.min(95, Math.round((loaded / total) * 95)),
                            Math.round((loaded / total) * metadata.size),
                        );
                },
            );
            const file = await decryptServerFile(
                this.key,
                blob,
                metadata,
                () => undefined,
                () => this.check(controller),
            );
            this.check(controller);
            this.callbacks.receiveProgress(id, 100, file.size);
            this.callbacks.received(id, file);
            delivered = true;
            // Do not discard a verified local file if the acknowledgment fails.
            // The idempotent receipt can safely be retried after a lost response.
            await this.api('received', { objectId }).catch(() =>
                this.api('received', { objectId }),
            );
        } catch (err) {
            if (!this.disposed && !delivered) {
                this.callbacks.error(
                    id,
                    err instanceof Error
                        ? err
                        : new Error('Could not receive file.'),
                );
                void this.api('failed', { objectId }).catch(() => undefined);
            }
        } finally {
            this.downloads.delete(controller);
        }
    }

    public cancelTransfer(id: number) {
        const operation = this.sends.get(id);
        operation?.controller.abort();
        if (operation?.objectId)
            void this.api('cancel', { objectId: operation.objectId }).catch(
                () => undefined,
            );
    }

    public destroy() {
        this.disposed = true;
        this.subscription.unsubscribe();
        for (const id of Array.from(this.sends.keys())) this.cancelTransfer(id);
        this.downloads.forEach((controller) => controller.abort());
        this.receiptWaiters.forEach((finish) => finish('cancelled'));
    }
}
